<script setup lang="ts">
import { __ } from '@@/bits/intl'
import OzDropdownButton, { OzDropdownButtonSizePreset } from '@@/library/v4/components/OzDropdownButton.vue'
import type { OzMenuRowsRow, OzMenuRowsSizePreset } from '@@/library/v4/components/OzMenuRows.vue'
import OzPopover from '@@/library/v4/components/OzPopover.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWindowSizeStore } from '@@/pinia/window_size'
import SurfaceDropdown from '@@/vuecomponents/SurfaceDropdown.vue'
import { shiftUpWhenOverflow } from '@@/vuecomposables/floating'
import type { ComputePositionConfig } from '@floating-ui/vue'
import { offset } from '@floating-ui/vue'
import { uniqueId } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

defineProps<{
  fieldLabel: string
  fieldLabelDescription?: string
  dropdownLabel: string
  dropdownSizePreset?: OzMenuRowsSizePreset
  buttonText: string
  rows: OzMenuRowsRow[]
  darkMode: boolean
  buttonTestId?: string
  dropdownTestId?: string
}>()

const { dir } = storeToRefs(useSurfaceStore())
const { windowHeight } = storeToRefs(useWindowSizeStore())

const isDropdownOpen = ref(false)
const emit = defineEmits(['select-row'])
const onRowSelect = (event, payload) => {
  isDropdownOpen.value = false
  emit('select-row', event, payload)
}

const button = ref<InstanceType<typeof OzDropdownButton> | null>(null)
const buttonEl = computed(() => button.value?.$el as HTMLElement)

const MAX_POPOVER_HEIGHT = computed(() => windowHeight.value - 12 * 2)

const popoverConfig: ComputePositionConfig = {
  middleware: [offset(4), shiftUpWhenOverflow()],
  placement: 'bottom-end',
}

const dropdownValueDescriptionId = uniqueId('dropdown-value-description')
</script>

<template>
  <div class="flex flex-row justify-between items-center w-full py-3">
    <div class="flex flex-col flex-1 pe-3">
      <p :class="['text-body', darkMode ? 'text-light-text-100' : 'text-dark-text-100']">
        {{ fieldLabel }}
      </p>
      <p
        v-if="fieldLabelDescription"
        :class="['text-body-small', darkMode ? 'text-light-text-200' : 'text-dark-text-200']"
      >
        {{ fieldLabelDescription }}
      </p>
    </div>
    <p :id="dropdownValueDescriptionId" class="sr-only">
      {{ __('The current %{fieldLabel} is %{fieldValue}.', { fieldLabel, fieldValue: buttonText }) }}
    </p>
    <div class="flex items-center justify-end">
      <OzDropdownButton
        ref="button"
        :dir="dir"
        :dark-mode="darkMode"
        :size-preset="OzDropdownButtonSizePreset.H32px"
        :text="buttonText"
        :data-testid="buttonTestId"
        aria-haspopup="menu"
        :aria-expanded="isDropdownOpen"
        :aria-describedby="dropdownValueDescriptionId"
        @click="isDropdownOpen = !isDropdownOpen"
      />
      <OzPopover
        v-if="isDropdownOpen"
        :anchor="buttonEl"
        :config="popoverConfig"
        :dark-mode="darkMode"
        @scrim-click="isDropdownOpen = false"
        @scrim-esc="isDropdownOpen = false"
      >
        <SurfaceDropdown
          :label="dropdownLabel"
          :rows="rows"
          :dark-mode="darkMode"
          :dir="dir"
          :class="['shadow-elevation-2', 'z-dropdown', 'w-64']"
          :size-preset="dropdownSizePreset"
          :container-style="{ maxHeight: `${MAX_POPOVER_HEIGHT}px` }"
          :data-testid="dropdownTestId"
          @exit="isDropdownOpen = false"
          @select-row="onRowSelect"
        />
      </OzPopover>
    </div>
  </div>
</template>
